import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import Modal from './Modal';
import Table from '../Listing/Table';
import PatientImage from '../Patient/PatientImage';
import { faUsers } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../api';

export default function InsurancePatientsModal({ show, onClose, insurance }) {
  const { t } = useTranslation();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !insurance?.id) return;
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/v1/getPatients`, {
      withCredentials: true,
      params: {
        page: 0,
        size: 1000,
        sortField: 'firstName',
        sortDirection: 'asc',
        healthInsurance: insurance.id,
      },
    })
      .then(response => setPatients(response.data.content))
      .catch(error => console.error('Failed to fetch insurance patients', error))
      .finally(() => setLoading(false));
  }, [show, insurance?.id]);

  const columns = [
    {
      Header: '#',
      accessor: 'id',
      sortable: true,
      className: 'text-center',
      width: 60,
      Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{row.id}</span>,
    },
    {
      Header: t('Patient'),
      accessor: 'firstName',
      sortable: true,
      Cell: (row) => (
        <div className="d-flex align-items-center gap-3">
          <PatientImage patient={row} size={36} />
          <NavLink to={`/patient/${row.id}`} className="cell-name">
            {row.firstName} {row.lastName}
          </NavLink>
        </div>
      ),
    },
    {
      Header: t('patients.column.cni'),
      accessor: 'cin',
      Cell: (row) => (!row.cin || row.cin === 'N/A')
        ? <span className="cell-muted">—</span>
        : <span className="cell-mono">{row.cin}</span>,
    },
    {
      Header: t('patients.column.status'),
      accessor: 'isPaymentUpToDate',
      className: 'text-center',
      Cell: (row) => {
        const ok = row.remainingToPay === 0;
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: ok ? '#ecfdf5' : '#fef2f2',
            color: ok ? '#065f46' : '#991b1b',
            padding: '4px 10px', borderRadius: 999,
            fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: ok ? '#10b981' : '#ef4444', flexShrink: 0 }} />
            {ok ? t('patients.status.up_to_date') : t('patients.status.overdue')}
          </span>
        );
      },
    },
    {
      Header: t('Orders'),
      accessor: 'ordersCount',
      className: 'text-center',
      sortable: true,
      Cell: (row) => (
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: row.ordersCount > 0 ? '#eff6ff' : '#f9fafb',
          color: row.ordersCount > 0 ? '#1d4ed8' : '#9ca3af',
          fontWeight: 600, fontSize: 13,
          padding: '3px 10px', borderRadius: 999, minWidth: 32,
        }}>
          {row.ordersCount}
        </span>
      ),
    },
  ];

  return (
    <Modal
      id="insurancePatientsModal"
      classSize="modal-lg"
      title={insurance ? t('insurances.patients_modal.title', { name: insurance.name }) : ''}
      show={show}
      onClose={onClose}
      icon={faUsers}
      iconBg="#eff6ff"
      iconColor="#1d4ed8"
      noBodyPadding
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            <strong style={{ color: '#111827' }}>{patients.length}</strong> {patients.length > 1 ? t('Patients') : t('Patient')}
          </span>
          <button type="button" className="btn btn-light btn-sm" onClick={onClose}>
            {t('Close')}
          </button>
        </div>
      }
    >
      <Table
        mode="client"
        rowId="id"
        columns={columns}
        data={patients}
        loading={loading}
        pageSize={20}
        emptyMessage={t('insurances.patients_modal.empty')}
      />
    </Modal>
  );
}
